import LocationOptions from '../utils/LocationOptions';
import {ExtendedFetchLocationsParams, FetchLocationsParams, LocationApi} from '../api';
import {HousingLocation, Paginated, SelectOptionType} from '../utils/types';
import {FetcherSettings} from '../api/apiClient';

class LocationService {
  private readonly locationApi: LocationApi;

  constructor() {
    this.locationApi = new LocationApi();
  }

  getAllCountries(resolverSettings?: FetcherSettings) {
    return this.locationApi.fetchAllCountries(resolverSettings);
  }

  getHousingLocation(id: string, resolverSettings?: FetcherSettings) {
    return this.locationApi.getByHousingId<HousingLocation>(id, resolverSettings);
  }

  private transformPaginatedListToOptions<T, D = undefined>(
    data: Promise<Paginated<T>>,
    optionsTransformer: (data: Paginated<T>) => SelectOptionType<D>[],
  ) {
    return data.then((res: Paginated<T>) => {
      return {
        ...res,
        results: optionsTransformer(res),
      };
    });
  }

  getCountriesList({
    phoneCodesOptions,
    ...params
  }: FetchLocationsParams & {phoneCodesOptions?: boolean}) {
    const result = this.locationApi.fetchCountriesList(params);
    return this.transformPaginatedListToOptions(
      result,
      phoneCodesOptions
        ? LocationOptions.getPhoneCodesAsOptions
        : LocationOptions.getCountriesAsOptions,
    );
  }

  getProvincesList(params: ExtendedFetchLocationsParams) {
    const result = this.locationApi.fetchProvincesList(params);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getProvincesAsOptions,
    );
  }

  getCitiesList(params: ExtendedFetchLocationsParams) {
    const result = this.locationApi.fetchCitiesList(params);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getCitiesAsOptions,
    );
  }

  getDistrictsList(params: ExtendedFetchLocationsParams) {
    const result = this.locationApi.fetchDistrictsList(params);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getDistrictsAsOptions,
    );
  }

  getMunicipalitiesList(params: ExtendedFetchLocationsParams) {
    const result = this.locationApi.fetchMunicipalitiesList(params);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getProvincesAsOptions,
    );
  }

  getPhoneCodesList(params: FetchLocationsParams) {
    const result = this.locationApi.fetchPhoneCodesList(params);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getPhoneCodesAsOptions,
    );
  }

  getSpanishResidenceCitiesList(
    params: FetchLocationsParams,
    resolverSettings?: FetcherSettings,
  ) {
    const result = this.locationApi.fetchSpanishResidenceCitiesList(
      params,
      resolverSettings,
    );
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getSpanishCitiesAsOptions,
    );
  }

  getLocationsByExternalUrl(
    params: FetchLocationsParams & {url: string},
    resolverSettings?: FetcherSettings,
  ) {
    const result = this.locationApi.fetchLocationsByExternalUrl(params, resolverSettings);
    return this.transformPaginatedListToOptions(
      result,
      LocationOptions.getSpanishCitiesAsOptions,
    );
  }
}

export {LocationService};
